import type { TreeDataNode } from "antd";
import { Tree } from "antd";
import type { DirectoryTreeProps } from "antd/lib/tree";
import type React from "react";
import { Spinner } from "react-bootstrap";
import type { Case, ExternalFolder } from "@/briefpoint-client";
import Button from "@/components/Button";
import Loading from "@/components/Loading";
import type { ExternalDocumentResponse } from "@/lib/types";
import { TreeSwitcherIcon } from "@/utils/ExternalPartner/utils";
import styles from "./ExternalFileExport.module.scss";

const { DirectoryTree } = Tree;

type ExternalWrapperProps = {
  children: React.ReactNode;
  directoryTitle: string;
  treeData: TreeDataNode[];
  files?: ExternalDocumentResponse[];
  destination?: ExternalFolder;
  isExporting?: boolean;
  isLoading: boolean;
  isSubmitted?: boolean;
  selectedCase?: Case;
  multiple?: boolean;
  submitActionText: string;
  additionalActionText?: string;
  areFilesAvailable?: boolean | null;
  onSelect: DirectoryTreeProps["onSelect"];
  expandedKeys?: React.Key[];
  onExpand?: DirectoryTreeProps["onExpand"];
  closeModal: () => void;
  additionalAction?: () => void;
  submitAction: () => void;
};

function ExternalDirectoryWrapper(props: ExternalWrapperProps) {
  const renderDirectory = () => {
    if (props.selectedCase) {
      if (props.areFilesAvailable && props.treeData.length > 0) {
        return (
          <div className={styles.fileTree}>
            <div>
              <DirectoryTree
                showLine
                disabled={props.isLoading}
                expandAction="doubleClick"
                multiple={Boolean(props.multiple)}
                className={styles.fileDirectory}
                onSelect={props.onSelect}
                expandedKeys={props.expandedKeys}
                onExpand={props.onExpand}
                defaultExpandParent
                defaultExpandAll
                switcherIcon={TreeSwitcherIcon}
                treeData={props.treeData}
              />
            </div>
          </div>
        );
      }
      return (
        <Loading
          isLoading={props.isLoading && props.areFilesAvailable === null}
        >
          {props.isExporting
            ? `No directories found. Please create a directory in your ${props.selectedCase?.integration?.partner} account for this case.`
            : "No files found for this case."}
        </Loading>
      );
    }

    return "Please select a case to view files.";
  };

  return (
    <div className={styles.fileWindowContainer}>
      <h2>{props.directoryTitle} </h2>
      <p className={styles.fileCase}>{props.selectedCase?.shortTitle}</p>
      {props.children}
      <div className={styles.fileWindow}>{renderDirectory()}</div>
      {!props.directoryTitle.includes("Add to") && (
        <small>
          Hold Ctrl(Windows) / Command(Mac) to select multiple files.
        </small>
      )}
      <div className={styles.fileWindowBtnWrapper}>
        <div className={styles.uploadCreateFolder}>
          {props.additionalAction && (
            <Button
              variant="outline"
              onClick={props.additionalAction}
              disabled={props.destination && !props.destination?.id}
            >
              {props.additionalActionText}
            </Button>
          )}
        </div>
        <div className={styles.uploadActions}>
          <Button variant="ghost" onClick={props.closeModal}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={props.submitAction}
            disabled={
              props.isSubmitted ||
              (props.files && !props.files?.length) ||
              (props.destination && !props.destination?.id)
            }
          >
            {props.isSubmitted ? (
              <>
                <Spinner size="sm" /> Adding...
              </>
            ) : (
              props.submitActionText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ExternalDirectoryWrapper;
